import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip/Tooltip';
import { FileIcon } from '@orn/shared-web/icons';
import formatPacificDateTime from '@orn/shared-web/utils/datetime/formatPacificDateTime';
import * as React from 'react';
import { Focusable } from 'react-aria-components';
import { Link, useNavigate } from 'react-router-dom';

import { AdminWorkItemResolution } from '../../__generated__/globalTypes';
import { iconButtonClassName } from '../../components/IconButton';
import InspectLink from '../../components/InspectLink';
import Table from '../../components/Table';
import { openRowInNewContext } from '../../lib/openRowInNewContext';
import { renderWorkItemBody } from '../../lib/renderWorkItemBody';
import { assistantThreadDeepLinkPath, objectInspectorPath } from '../../paths';
import WorkItemJournalModal from '../WorkItems/WorkItemJournalModal';
import { WORK_ITEM_CATEGORY_LABEL } from '../WorkItems/workItemsView';

import { Absent, HeldBadge, ResolutionMark, STACKED_TIMESTAMP_COLUMN_CLASS, Timestamp } from './AccountFields';
import { groupWorkItemsByAssignee, lastWorkItemNudge } from './accountsView';

import type { AdminGetFamilyAccountQuery } from './__generated__/AccountsScene.gql';
import type { IWorkItemLastNudge } from './accountsView';

type OpenWorkItems = NonNullable<AdminGetFamilyAccountQuery['adminGetFamilyAccount']>['openWorkItems'];
type OpenWorkItem = OpenWorkItems['items'][number];

/** What the hold means on a still-open item: current, and a caregiver's to end. */
const ITEM_HELD_TITLE = "Off a loved one's read until a caregiver releases it.";

/**
 * Said in words rather than shown as an empty tooltip, so a card the assistant
 * gave a heading and no detail is distinguishable from one whose tooltip broke.
 */
const NO_BODY_DESCRIPTION = 'This card carries no detail under its heading.';

const JOURNAL_DESCRIPTION =
  'Every note the assistant or a household member wrote on this item, newest first, with the thread each came from.';

interface IOpenWorkItemsPanelProps {
  openWorkItems: OpenWorkItems;
  /** The household the items belong to, which the journal's thread links open under. */
  familyId: string;
}

/**
 * The household's open work, gathered under whoever owes each item.
 *
 * Only items the assistant has written a card for. An item still in its
 * private working set has no heading to show, so it would read as a blank row
 * under whichever assignee holds it; the Work items tool is where that set is
 * read, and the Household tools row links out to it.
 *
 * Carries no heading of its own: it fills the Work items tab, whose label and
 * count already name it. Only a panel sharing a tab needs one.
 */
const OpenWorkItemsPanel = ({ openWorkItems, familyId }: IOpenWorkItemsPanelProps) => {
  const groups = groupWorkItemsByAssignee(openWorkItems.items);
  const [journalItemId, setJournalItemId] = React.useState<string | null>(null);

  return (
    <section className="flex min-w-0 flex-col gap-3 overflow-x-auto">
      {groups.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No open work items on this household.</div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.assigneeUserId ?? 'assistant'} className="flex flex-col gap-1">
              <h3 className="text-fg-tertiary flex items-baseline gap-2 text-xs uppercase tracking-wider">
                {group.assigneeUserId === null ? <span className="italic">{group.label}</span> : group.label}
                <span className="text-fg-quaternary normal-case tracking-normal">{group.items.length}</span>
              </h3>
              {/* Past the fixed columns' 59.5rem, the synopsis keeps 16rem before the panel scrolls sideways. */}
              <Table className="min-w-[75.5rem] table-fixed">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell className="w-[5.5rem]">Id</Table.HeaderCell>
                    <Table.HeaderCell>Synopsis</Table.HeaderCell>
                    <Table.HeaderCell className="w-[6rem]">Category</Table.HeaderCell>
                    <Table.HeaderCell className={STACKED_TIMESTAMP_COLUMN_CLASS}>Updated</Table.HeaderCell>
                    <Table.HeaderCell className={STACKED_TIMESTAMP_COLUMN_CLASS}>Due</Table.HeaderCell>
                    <Table.HeaderCell className={STACKED_TIMESTAMP_COLUMN_CLASS}>Last Nudge</Table.HeaderCell>
                    <Table.HeaderCell className={STACKED_TIMESTAMP_COLUMN_CLASS}>Next Nudge</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {group.items.map((item) => (
                    <WorkItemRow key={item.id} item={item} onOpenJournal={setJournalItemId} />
                  ))}
                </Table.Body>
              </Table>
            </div>
          ))}
        </div>
      )}

      {openWorkItems.truncated && (
        <div className="text-fg-tertiary text-xs italic">
          Showing the first {openWorkItems.totalReturned} — the rest are in the Work items tool.
        </div>
      )}

      <WorkItemJournalModal
        workItem={journalItemId === null ? null : { id: journalItemId, familyId }}
        onClose={() => setJournalItemId(null)}
      />
    </section>
  );
};

interface IWorkItemRowProps {
  item: OpenWorkItem;
  onOpenJournal: (workItemId: string) => void;
}

const WorkItemRow = ({ item, onOpenJournal }: IWorkItemRowProps) => {
  const navigate = useNavigate();
  const to = objectInspectorPath(item.id);

  return (
    <Table.Row onClick={(event) => openRowInNewContext(event, to, navigate)} className="cursor-pointer">
      {/* The id link navigates on its own, so it must not reach the row. */}
      <Table.Cell className="text-xs" onClick={(event) => event.stopPropagation()}>
        <InspectLink id={item.id} short />
      </Table.Cell>
      <Table.Cell>
        {/* Icons then title start the cell so every row reads off one left edge;
            the hold rides the far side, where it scans as a column of its own. */}
        <div className="flex min-w-0 items-baseline gap-2">
          <ResolutionMark resolution={AdminWorkItemResolution.Open} resolvedAt={null} />
          {/* The journal opens in place, so its click must not reach the row. */}
          <span className="flex shrink-0 self-center" onClick={(event) => event.stopPropagation()}>
            <Tooltip title="Journal" description={JOURNAL_DESCRIPTION} placement="top">
              <TooltipTrigger
                aria-label="Open the journal"
                onPress={() => onOpenJournal(item.id)}
                // The trigger's `outline-hidden` otherwise leaves the focus ring with no style to draw.
                className={iconButtonClassName('neutral', 'focus-visible:outline-solid')}
              >
                <FileIcon size={14} aria-hidden />
              </TooltipTrigger>
            </Tooltip>
          </span>
          <WorkItemTitle title={item.title} body={item.body ?? null} to={to} />
          {item.caregiverOnly && (
            <span className="ml-auto">
              <HeldBadge description={ITEM_HELD_TITLE} />
            </span>
          )}
        </div>
      </Table.Cell>
      <Table.Cell muted className="text-[10px] uppercase tracking-wide">
        {item.category === null || item.category === undefined ? (
          <Absent>None</Absent>
        ) : (
          WORK_ITEM_CATEGORY_LABEL[item.category]
        )}
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        <Timestamp instant={item.updatedAt} stacked />
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        {item.dueDate === null || item.dueDate === undefined ? (
          <Absent>None</Absent>
        ) : (
          <Timestamp instant={item.dueDate} stacked />
        )}
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        <LastNudge nudge={lastWorkItemNudge(item)} userId={item.assigneeUserId ?? null} />
      </Table.Cell>
      <Table.Cell muted className="text-xs">
        {item.nudgeDueAt === null || item.nudgeDueAt === undefined ? (
          <Absent>None</Absent>
        ) : (
          <Timestamp instant={item.nudgeDueAt} stacked />
        )}
      </Table.Cell>
    </Table.Row>
  );
};

interface ILastNudgeProps {
  nudge: IWorkItemLastNudge | null;
  /** The assignee, whose threads the Threads tool lists beside the one it opens. */
  userId: string | null;
}

/**
 * When the last nudge went out, linked to the thread it went out in, or
 * "Skipped" with its time on hover. A skip sends nothing, so it has no thread.
 */
const LastNudge = ({ nudge, userId }: ILastNudgeProps) => {
  if (nudge === null) {
    return <Absent>None</Absent>;
  }
  if (nudge.outcome === 'skipped') {
    return <span title={`Skipped ${formatPacificDateTime(nudge.at)}`}>Skipped</span>;
  }
  if (nudge.threadId === null) {
    return <Timestamp instant={nudge.at} stacked />;
  }
  return (
    <Tooltip title="Click to view thread" placement="top">
      <Focusable>
        {/* The row navigates too, so its handler must not fire a second time. */}
        <Link
          to={assistantThreadDeepLinkPath({ userId: userId ?? undefined, threadId: nudge.threadId })}
          onClick={(event) => event.stopPropagation()}
          className="text-fg-brand-primary hover:text-fg-brand-secondary block cursor-pointer underline-offset-2 hover:underline"
        >
          <Timestamp instant={nudge.at} stacked />
        </Link>
      </Focusable>
    </Tooltip>
  );
};

interface IWorkItemTitleProps {
  title: string;
  body: string | null;
  to: string;
}

/**
 * The card's heading, as a real anchor to the item in the object inspector.
 *
 * An anchor rather than the tooltip's own button trigger: a button cannot be
 * command-clicked into a new tab, offers no context menu, and draws the arrow
 * cursor. `Focusable` is what lets the tooltip attach to something that is not
 * one, so the detail on hover survives the change.
 */
const WorkItemTitle = ({ title, body, to }: IWorkItemTitleProps) => (
  <Tooltip title={title} description={renderWorkItemBody(body) ?? NO_BODY_DESCRIPTION} placement="top">
    <Focusable>
      {/* The row navigates too, so its handler must not fire a second time. */}
      <Link to={to} onClick={(event) => event.stopPropagation()} className="min-w-0 flex-1 cursor-pointer">
        <span className="block truncate text-left">{title}</span>
      </Link>
    </Focusable>
  </Tooltip>
);

export default OpenWorkItemsPanel;
